import Summary from "./_components/summary";
import BookingOptions from "./_components/booking-options";
import useBookingStore, { BookingMethod } from "@/store/booking-store";
import { useEffect, useState } from "react";
import Loader from "@/components/loader";
import { useTranslations } from "../../../../i18n";
import toast from "react-hot-toast";
import { useViewStore } from "@/store/view-store";
import { Button } from "@heroui/react";
import { useCheckoutPayment } from "@/hooks/use-checkout-payment";
import EmbeddedStripeCheckout from "./_components/EmbeddedStripeCheckout";
import { api } from "@/lib/api";
import { withLocale } from "@/lib/widget-locale";
import { EventBooking } from "./_components/event-booking";
import PaymentMethodsSelection from "./_components/payment-methods";
import TwintQrPayment from "./_components/twint-qr-payment";
import { useMediaQuery } from "@/hooks/use-media-query";

export default function BookingPage() {
  const t = useTranslations("checkout");
  const tCommon = useTranslations("common");

  const {
    clientSecret,
    setClientSecret,
    bookingId,
    pay,
    isLoading,
    isBookingValid,
    buttonLabel,
    twintPendingBookingId,
    confirmTwintPayment,
  } = useCheckoutPayment();

  const isMobile = useMediaQuery("(max-width: 767px)");
  const [mobileStep, setMobileStep] = useState<"options" | "payment" | "summary">("options");

  const [loading, setLoading] = useState(true);
  const {
    setSelectedEvent: setBookingStoreEvent,
    setSelectedUserPass,
    setSelectedPass,
    bookingMethod,
    selectedPass,
  } = useBookingStore();
  const navigate = useViewStore((s) => s.navigate);
  const onBookingSuccess = useViewStore((s) => s.onBookingSuccess);
  const selectedEventViewStore = useViewStore((s) => s.selectedEvent);

  const needsPaymentStep =
    bookingMethod === BookingMethod.NEW_PASS &&
    !!selectedPass &&
    (selectedPass as any).price > 0;

  const handleContinueFromOptions = () => {
    // Read fresh store state — Zustand updates synchronously but React hasn't re-rendered
    // yet when onSelect is called right after setBookingMethod in BookingOptions.
    const { bookingMethod: currentMethod, selectedPass: currentPass } =
      useBookingStore.getState();
    const requiresPayment =
      currentMethod === BookingMethod.NEW_PASS &&
      !!currentPass &&
      (currentPass as any).price > 0;
    setMobileStep(requiresPayment ? "payment" : "summary");
  };

  const handleMobileBack = () => {
    if (mobileStep === "summary") {
      const { bookingMethod: currentMethod, selectedPass: currentPass } =
        useBookingStore.getState();
      const requiresPayment =
        currentMethod === BookingMethod.NEW_PASS &&
        !!currentPass &&
        (currentPass as any).price > 0;
      setMobileStep(requiresPayment ? "payment" : "options");
    } else if (mobileStep === "payment") {
      setMobileStep("options");
    } else {
      setSelectedUserPass(null);
      setSelectedPass(null);
      navigate("calendar");
    }
  };

  const mobileSteps = needsPaymentStep
    ? (["options", "payment", "summary"] as const)
    : (["options", "summary"] as const);
  const currentMobileStepIndex = mobileSteps.indexOf(mobileStep as any);

  useEffect(() => {
    async function fetchData() {
      try {
        setLoading(true);

        if (!selectedEventViewStore) {
          return;
        }
        // Fetch event details TODO
        const eventResult = await api(
          withLocale(`/api/widget/class-sessions/${selectedEventViewStore.id}`),
        );
        if (eventResult?.error || !eventResult?.session) {
          toast.error(eventResult?.error || t("somethingWentWrong"));
        } else {
          setBookingStoreEvent({
            ...eventResult.session,
            pricing: eventResult.pricing,
            teacher: eventResult.teacher,
          });
        }
      } catch (error) {
        toast.error(t("somethingWentWrong") || "Something went wrong");
        console.error(error);
      } finally {
        setLoading(false);
      }
    }

    fetchData();
  }, [selectedEventViewStore]);

  if (!selectedEventViewStore) {
    return null;
  }

  if (loading) {
    return (
      <div className="flex justify-cente items-center h-64">
        <Loader />
      </div>
    );
  }

  if (twintPendingBookingId) {
    return (
      <div className="max-w-7xl mx-auto px-3 py-3 md:py-8">
        <TwintQrPayment onComplete={confirmTwintPayment} />
      </div>
    );
  }

  const isRegularClass =
    selectedEventViewStore.type !== "WORKSHOP" &&
    selectedEventViewStore.type !== "RETREAT";

  return (
    <div className="max-w-7xl mx-auto px-3 py-3 md:py-8">
      <Button
        size="md"
        variant="light"
        color="primary"
        startContent={<span aria-hidden="true">←</span>}
        onPress={
          isMobile && isRegularClass && !clientSecret
            ? handleMobileBack
            : () => {
                setSelectedUserPass(null);
                setSelectedPass(null);
                navigate("calendar");
              }
        }
      >
        {tCommon("back")}
      </Button>

      {!isRegularClass ? (
        <EventBooking />
      ) : (
        <>
          {!clientSecret && (
            <>
              {/* Mobile: step-by-step */}
              {isMobile && (
                <div className="mt-3 flex flex-col gap-4">
                  {/* Step dots */}
                  <div className="flex items-center justify-center gap-2 mt-1">
                    {mobileSteps.map((_, i) => (
                      <div
                        key={i}
                        className={`h-2 rounded-full transition-all ${
                          i === currentMobileStepIndex
                            ? "w-6 bg-primary"
                            : i < currentMobileStepIndex
                              ? "w-2 bg-primary opacity-50"
                              : "w-2 bg-gray-300"
                        }`}
                      />
                    ))}
                  </div>

                  {mobileStep === "options" && (
                    <BookingOptions
                      hidePaymentMethods
                      autoSelectDefault={false}
                      onSelect={handleContinueFromOptions}
                    />
                  )}

                  {mobileStep === "payment" && (
                    <>
                      <PaymentMethodsSelection />
                      <Button
                        color="primary"
                        size="lg"
                        className="w-full"
                        onPress={() => setMobileStep("summary")}
                      >
                        {tCommon("continue")}
                      </Button>
                    </>
                  )}

                  {mobileStep === "summary" && (
                    <Summary
                      pay={pay}
                      isLoading={isLoading}
                      isBookingValidHook={isBookingValid}
                      buttonLabel={buttonLabel}
                    />
                  )}
                </div>
              )}

              {/* Desktop: side-by-side */}
              {!isMobile && (
                <div className="grid grid-cols-1 md:grid-cols-3 gap-5 md:gap-8 mt-3 md:mt-8">
                  <div className="col-span-1 md:col-span-2 flex flex-col gap-5">
                    <BookingOptions />
                  </div>
                  <Summary
                    pay={pay}
                    isLoading={isLoading}
                    isBookingValidHook={isBookingValid}
                    buttonLabel={buttonLabel}
                  />
                </div>
              )}
            </>
          )}

          {clientSecret && (
            <div className="w-full md:w-[75%] mx-auto flex flex-col justify-center items-center">
              <Button
                variant="light"
                color="primary"
                onPress={() => setClientSecret(null)}
                className="mb-4"
                startContent={<span aria-hidden="true">←</span>}
              >
                {tCommon("cancel-payment")}
              </Button>

              <EmbeddedStripeCheckout
                clientSecret={clientSecret}
                onComplete={() => {
                  onBookingSuccess(bookingId as string);
                  setClientSecret(null);
                }}
              />
            </div>
          )}
        </>
      )}
    </div>
  );
}
